import { atom, useAtom, useAtomValue } from 'jotai';
import { Classroom } from 'types/classroom';
import { OnSuccessFct, SubmitPayload } from './types';
import { Schoolyear } from 'types/schoolyear';
import { School } from 'types/school';
import { CJEvent } from 'types/event';
import { setHoursAndMinutes } from 'utils/time/setHoursAndMinutes';

interface CopyEventsToAnotherDayPayload {
  from?: Date;
  to?: Date;
  classrooms: Classroom[];
  ctaTitle?: (v: Date, nbOfSelectedSeances?: number) => void;
  selectedSeances?: CJEvent[];
  onSuccess?: OnSuccessFct;
}

export const copyEventsToAnotherDayPayload = atom<
  CopyEventsToAnotherDayPayload | undefined
>(undefined);

export const useOpenCopyEventsToAnotherDayModal = () => {
  const [, setPayload] = useAtom(copyEventsToAnotherDayPayload);
  return (payload: CopyEventsToAnotherDayPayload) => setPayload(payload);
};
export const useCopyEventsToAnotherDayPayload = () =>
  useAtomValue(copyEventsToAnotherDayPayload);

export const useCloseCopyEventsToAnotherDayModal = () => {
  const [, setPayload] = useAtom(copyEventsToAnotherDayPayload);
  return () => setPayload(undefined);
};

export const selectedSchoolSchoolyearAtom = atom<{
  srcSchoolyear?: Schoolyear;
  destSchoolyear?: Schoolyear;
  srcSchool?: School;
  destSchool?: School;
}>({});

export const useSetSrcSchool = () => {
  const [, setSelectedSchoolSchoolyear] = useAtom(selectedSchoolSchoolyearAtom);
  return (school: School) =>
    setSelectedSchoolSchoolyear(prev => ({ ...prev, srcSchool: school }));
};
export const useSetDestSchool = () => {
  const [, setSelectedSchoolSchoolyear] = useAtom(selectedSchoolSchoolyearAtom);
  return (school: School) =>
    setSelectedSchoolSchoolyear(prev => ({ ...prev, destSchool: school }));
};
export const useSetSrcSchoolyear = () => {
  const [, setSelectedSchoolSchoolyear] = useAtom(selectedSchoolSchoolyearAtom);
  return (schoolyear: Schoolyear) =>
    setSelectedSchoolSchoolyear(prev => ({
      ...prev,
      srcSchoolyear: schoolyear,
    }));
};
export const useSetDestSchoolyear = () => {
  const [, setSelectedSchoolSchoolyear] = useAtom(selectedSchoolSchoolyearAtom);
  return (schoolyear: Schoolyear) =>
    setSelectedSchoolSchoolyear(prev => ({
      ...prev,
      destSchoolyear: schoolyear,
    }));
};
export const useSrcSchoolSchoolyear = () => {
  const [selectedSchoolSchoolyear] = useAtom(selectedSchoolSchoolyearAtom);
  return {
    schoolyear: selectedSchoolSchoolyear.srcSchoolyear,
    school: selectedSchoolSchoolyear.srcSchool,
  };
};

export const useDestSchoolSchoolyear = () => {
  const [selectedSchoolSchoolyear] = useAtom(selectedSchoolSchoolyearAtom);
  return {
    schoolyear: selectedSchoolSchoolyear.destSchoolyear,
    school: selectedSchoolSchoolyear.destSchool,
  };
};

export const submitPayloadAtom = atom<SubmitPayload>({
  from: new Date(),
  to: new Date(),
});
export const useFromValue = () => {
  const [payload] = useAtom(submitPayloadAtom);
  return payload.from;
};
export const useToValue = () => {
  const [payload] = useAtom(submitPayloadAtom);
  return payload.to;
};

export const useSetFromValue = () => {
  const [, setPayload] = useAtom(submitPayloadAtom);
  return (from: Date) => setPayload(prev => ({ ...prev, from }));
};
export const useSetToValue = () => {
  const [, setPayload] = useAtom(submitPayloadAtom);
  return (to: Date) => setPayload(prev => ({ ...prev, to }));
};

export const selectedPreviewSeancesAtom = atom<CJEvent[]>([]);

const resetSelectedPreviewSeancesAtom = atom(null, (_, set) => {
  set(selectedPreviewSeancesAtom, []);
});

const singleSelectedSeanceAtom = atom(get => {
  const selectedSeances = get(selectedPreviewSeancesAtom);
  if (selectedSeances.length === 1) {
    return selectedSeances[0];
  }
  return undefined;
});

export const useSingleSelectedSeance = () => {
  const hasOneSeanceSelected = useAtomValue(singleSelectedSeanceAtom);
  return hasOneSeanceSelected;
};

export const useResetSelectedPreviewSeances = () => {
  const [, resetSelectedPreviewSeances] = useAtom(
    resetSelectedPreviewSeancesAtom,
  );
  return resetSelectedPreviewSeances;
};

export const useCountSelectedSeances = () => {
  const [selectedSeances] = useAtom(selectedPreviewSeancesAtom);

  return selectedSeances.length;
};

export const useSelectedPreviewSeances = () => {
  const [selectedSeances] = useAtom(selectedPreviewSeancesAtom);

  return selectedSeances;
};

export const useClickOnPreviewSeance = () => {
  const [, setSelectedPreviewSeances] = useAtom(selectedPreviewSeancesAtom);
  const toValue = useToValue();
  const updateToValue = useSetToValue();

  return (seance: CJEvent) => {
    setSelectedPreviewSeances(prevSelected => {
      const prevSelectedIds = prevSelected.map(ev => ev.id);
      if (prevSelectedIds.includes(seance?.id)) {
        // Remove the seanceId if it's already selected
        const newSelectedSeances = prevSelected.filter(
          ev => ev.id !== seance?.id,
        );
        if (newSelectedSeances.length === 1 && newSelectedSeances[0].start) {
          updateToValue(
            setHoursAndMinutes(toValue, newSelectedSeances[0].start),
          );
        }
        return newSelectedSeances;
      }
      if (prevSelected.length === 0 && seance?.start) {
        updateToValue(setHoursAndMinutes(toValue, seance.start));
      }
      // Add the seanceId if it's not selected
      return [...prevSelected, seance];
    });
  };
};
export const useIsSeancePreviewSelected = (seanceId: string) => {
  const selectedSeances = useAtomValue(selectedPreviewSeancesAtom);
  return selectedSeances.map(ev => ev.id).includes(seanceId);
};
